import { queryLog } from "@/db/audit";
import { getProfile, listProfiles } from "@/db/profiles";
import { getSnapshotRows, listSnapshots } from "@/db/snapshots";
import { downloadText } from "@/lib/download";
import type {
  AuditLogEntry,
  CatalogProfile,
  NormalizedRow,
  SnapshotMeta,
} from "@/types/schemas";

/**
 * Plain (unencrypted) JSON export of a profile and its snapshots. This is the
 * portable, human-readable local export; the encrypted `.mspcatalogkit` bundle
 * lands in a later v0.2 prompt. Everything happens in the browser — no upload.
 */

export interface ExportedSnapshot {
  meta: SnapshotMeta;
  rows: NormalizedRow[];
}

export interface ProfileExportBundle {
  format: "catalogkit-profile-export";
  version: 1;
  exported_at: string;
  profile: CatalogProfile;
  snapshots: ExportedSnapshot[];
  audit_log: AuditLogEntry[];
}

/** Gather a profile, its (decompressed) snapshots, and its audit trail. */
export async function buildProfileExport(profileId: string): Promise<ProfileExportBundle> {
  const profile = await getProfile(profileId);
  if (profile === null) throw new Error(`Profile not found: ${profileId}`);

  const metas = await listSnapshots(profileId);
  const snapshots: ExportedSnapshot[] = [];
  for (const meta of metas) {
    snapshots.push({ meta, rows: await getSnapshotRows(meta.id) });
  }

  return {
    format: "catalogkit-profile-export",
    version: 1,
    exported_at: new Date().toISOString(),
    profile,
    snapshots,
    audit_log: await queryLog({ profileId }),
  };
}

/** File-safe slug from a profile label. */
export function safeFilename(label: string): string {
  return (
    label
      .trim()
      .replace(/[^a-z0-9._-]+/gi, "_")
      .replace(/^_+|_+$/g, "") || "profile"
  );
}

/** Build and download a profile export as a JSON file. */
export async function exportProfileToFile(profileId: string): Promise<ProfileExportBundle> {
  const bundle = await buildProfileExport(profileId);
  downloadText(
    `catalogkit_${safeFilename(bundle.profile.label)}.json`,
    JSON.stringify(bundle, null, 2),
    "application/json",
  );
  return bundle;
}

export interface FullExportBundle {
  format: "catalogkit-full-export";
  version: 1;
  exported_at: string;
  profiles: ProfileExportBundle[];
}

/** Build and download an export of every profile (archived included). */
export async function exportAllToFile(): Promise<FullExportBundle> {
  const all = await listProfiles();
  const bundles: ProfileExportBundle[] = [];
  for (const profile of all) {
    bundles.push(await buildProfileExport(profile.id));
  }
  const full: FullExportBundle = {
    format: "catalogkit-full-export",
    version: 1,
    exported_at: new Date().toISOString(),
    profiles: bundles,
  };
  downloadText("catalogkit_all_profiles.json", JSON.stringify(full, null, 2), "application/json");
  return full;
}
